/**
 * jrs-v2-i18n.js — minimal client-side translation helper for the v2 customizer
 *
 * @version 0.1.0 (2026-06-05) — net-new infra per Doc 7 §2.3 ("client-side
 *   translation helper"). Built for the success step (success-step prompt
 *   decision 1, Option A); Phase 2–4 step controllers consume it too.
 *
 * Strings live under `customizer.*` in `locales/en.default.json`. A Liquid
 * snippet (`jrs-v2-i18n-bootstrap.liquid`) renders the needed keys into a
 * `<script type="application/json" data-jrs-i18n>` blob and calls
 * `JRS.i18n.init(...)` once at theme load. Controllers then read strings via
 * `JRS.i18n.t('customizer.foo', { count: 3 })` — never inline literals (FR8).
 *
 * Interpolation supports BOTH brace styles so it satisfies Doc 7 §2.3's
 * documented `{{ name }}` convention AND the FR8 / decision-1 `{ name }` keys
 * (e.g. "{count} items · £{subtotal} subtotal"). Both are handled in a single
 * pass with `{{ name }}` taking priority over `{ name }`.
 *
 * Fail-visible: an unknown key returns the key string (so a missing
 * translation is obvious in the UI rather than rendering blank), and an
 * unsupplied token is left intact.
 */

window.JRS = window.JRS || {};

(function (JRS) {
  'use strict';

  var _translations = {};

  /**
   * Initialise the translation table. Idempotent-by-replacement: the last
   * init() wins (the bootstrap snippet calls this once at load).
   * @param {Object<string,string>} translations - flat dot-key → string map
   */
  function init(translations) {
    _translations = (translations && typeof translations === 'object') ? translations : {};
  }

  /**
   * Resolve a key, interpolating {{ name }} and { name } tokens from params.
   * @param {string} key - flat dot-key, e.g. 'customizer.success.title'
   * @param {Object} [params] - token values, e.g. { count: 3, subtotal: '4.50' }
   * @returns {string} the resolved string, or the key itself if unknown
   */
  function t(key, params) {
    var value = _translations[key];
    if (typeof value !== 'string') return key; // fail-visible: surface the missing key
    if (!params) return value;
    // ONE pass over both brace styles. The double-brace alternative is listed
    // first, so at each position `{{ name }}` matches as a whole — the
    // single-brace rule never catches its inner `{ name }` (which a two-pass
    // approach would, corrupting an unsupplied double-brace token). An
    // unsupplied token is returned verbatim, preserving its original braces.
    return value.replace(
      /\{\{\s*(\w+)\s*\}\}|\{\s*(\w+)\s*\}/g,
      function (m, dbl, sgl) {
        var name = dbl !== undefined ? dbl : sgl;
        return params[name] !== undefined ? params[name] : m;
      }
    );
  }

  /**
   * @param {string} key
   * @returns {boolean} true when a string translation is present for key
   */
  function has(key) {
    return typeof _translations[key] === 'string';
  }

  JRS.i18n = { init: init, t: t, has: has };

  // Self-bootstrap from the page's data-jrs-i18n blob (rendered by
  // jrs-v2-i18n-bootstrap.liquid). This file is deferred, so it executes AFTER
  // the document is parsed — the blob is in the DOM — which sidesteps the
  // ordering trap of an inline loader running before this deferred module.
  // init() stays public for tests and for a manual re-init if ever needed.
  try {
    var _tag = document.querySelector('[data-jrs-i18n]');
    if (_tag && _tag.textContent) {
      init(JSON.parse(_tag.textContent));
    }
  } catch (_e) {
    // Malformed / absent blob — t() stays fail-visible (returns the key).
  }
})(window.JRS);
